/**
 * An agent's Knowledge: each step's own Knowledge Base notes (ENG-359). A step reads the main
 * Knowledge Base and its own notes, and saves what it learns to its own notes (kb_remember);
 * nobody else's runs, and not the Knowledge Base page, see them. Here people can read them,
 * correct or remove a wrong one, and add notes to teach a step.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { showToast } from '@/services/toast.service';
import type { Member } from './catalog';
import { errorText } from './data';

type Note = { id: string; agent_id: string; name: string; body: string; created_by: string | null; updated_by: string | null; updated_at: string | null };

const api = () => ApiService.getInstance();
// A step's own runs write as def-<id>; anything else is a person.
const author = (n: Note) => {
  const who = n.updated_by ?? n.created_by ?? '';
  return who.startsWith('def-') || who.startsWith('agent:') ? 'the agent' : who || 'someone';
};

function NoteForm({ initial, submitLabel, pending, onSubmit, onCancel }: {
  initial?: { name: string; body: string };
  submitLabel: string;
  pending: boolean;
  onSubmit: (n: { name: string; body: string }) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  return (
    <div className="space-y-2">
      <Input value={name} placeholder="Title" aria-label="Note title" onChange={(e) => setName(e.target.value)} />
      <Textarea value={body} className="min-h-[120px]" placeholder="What this step should know" aria-label="Note" onChange={(e) => setBody(e.target.value)} />
      <div className="flex gap-2">
        <Button size="sm" disabled={pending || !name.trim() || !body.trim()} onClick={() => onSubmit({ name: name.trim(), body })}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function NoteRow({ teamId, note }: { teamId: string; note: Note }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['agent-teams', teamId, 'knowledge'] });
  const save = useMutation({
    mutationFn: async (n: { name: string; body: string }) => api().request('PUT', `/agent-teams/${teamId}/knowledge/${note.id}`, n),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
    onError: (e) => showToast.error(errorText(e, 'Could not save the note.')),
  });
  const remove = useMutation({
    mutationFn: async () => api().request('DELETE', `/agent-teams/${teamId}/knowledge/${note.id}`),
    onSuccess: refresh,
    onError: (e) => showToast.error(errorText(e, 'Could not delete the note.')),
  });
  if (editing) {
    return (
      <li className="rounded border p-3">
        <NoteForm initial={note} submitLabel="Save" pending={save.isPending} onSubmit={(n) => save.mutate(n)} onCancel={() => setEditing(false)} />
      </li>
    );
  }
  return (
    <li className="rounded border p-3 space-y-1 text-sm">
      <div className="flex items-center gap-2">
        <button className="font-medium text-left hover:underline" onClick={() => setOpen(!open)}>
          {note.name}
        </button>
        <span className="text-xs text-muted-foreground">
          by {author(note)}
          {note.updated_at ? ` · ${new Date(note.updated_at).toLocaleDateString()}` : ''}
        </span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(`Delete "${note.name}"? The step forgets it.`)) remove.mutate();
          }}
        >
          Delete
        </Button>
      </div>
      <p className={`whitespace-pre-wrap text-muted-foreground ${open ? '' : 'line-clamp-2'}`}>{note.body}</p>
    </li>
  );
}

function StepNotes({ teamId, step, notes }: { teamId: string; step: Member; notes: Note[] }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const add = useMutation({
    mutationFn: async (n: { name: string; body: string }) => api().request('POST', `/agent-teams/${teamId}/knowledge`, { ...n, agent_id: step.id }),
    onSuccess: () => {
      setAdding(false);
      queryClient.invalidateQueries({ queryKey: ['agent-teams', teamId, 'knowledge'] });
    },
    onError: (e) => showToast.error(errorText(e, 'Could not add the note.')),
  });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center space-y-0">
        <CardTitle className="text-base">{step.name}</CardTitle>
        <span className="ml-3 text-xs text-muted-foreground">
          {notes.length} note{notes.length === 1 ? '' : 's'}
        </span>
        {!adding && (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3 mr-1" /> Add note
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {adding && <NoteForm submitLabel="Add note" pending={add.isPending} onSubmit={(n) => add.mutate(n)} onCancel={() => setAdding(false)} />}
        {notes.length ? (
          <ul className="space-y-2">
            {notes.map((n) => (
              <NoteRow key={n.id} teamId={teamId} note={n} />
            ))}
          </ul>
        ) : (
          !adding && <p className="text-sm text-muted-foreground">Nothing yet. It saves what it learns here as it works.</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AgentKnowledge({ teamId, steps }: { teamId: string; steps: Member[] }) {
  const apiReady = useApiAuthReady();
  const notes = useQuery<{ notes: Note[] }>({
    queryKey: ['agent-teams', teamId, 'knowledge'],
    queryFn: async () => (await api().request('GET', `/agent-teams/${teamId}/knowledge`)) as { notes: Note[] },
    enabled: apiReady,
  });
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">Knowledge</h2>
      <p className="text-sm text-muted-foreground">
        Each step reads the Knowledge Base and its own notes, which only it sees. It saves what it learns there as it works. Correct
        or remove a note that's wrong, or add one to teach it.
      </p>
      {notes.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {steps
        .filter((s) => s.id)
        .map((s) => (
          <StepNotes key={s.id} teamId={teamId} step={s} notes={(notes.data?.notes ?? []).filter((n) => n.agent_id === s.id)} />
        ))}
    </section>
  );
}
